import { useMemo } from 'react'

import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import { Decimal } from 'decimal.js'
import { PositionConfigInfo, Price, Amount, CoinInfo, PositionDecimalMath } from '@kunalabs-io/kai'
import { PositionCreationValues } from '../types'
import { calcEffectiveInterestRate } from 'utils/userUserPositionDisplayInfos'

type Config = ReturnType<
  PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>['configFromBcs']
>
type Pool = ReturnType<
  PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>['poolFromBcs']
>
type SupplyPool = ReturnType<
  PositionConfigInfo<
    PhantomTypeArgument,
    PhantomTypeArgument,
    TypeArgument
  >['supplyPoolXInfo']['fromBcs']
>

interface UsePositionCreationValuesArgs {
  pool: Pool | undefined
  config: Config | undefined
  configInfo: PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  supplyPoolX: SupplyPool | undefined
  supplyPoolY: SupplyPool | undefined
  tickA: number | undefined
  tickB: number | undefined
  leverage: number
  startL: bigint | undefined
  maxL: bigint | undefined
  maxLeverage: Decimal | undefined
  sanitizedXOnBlur: string
  sanitizedYOnBlur: string
}

export const usePositionCreationValues = ({
  pool,
  config,
  configInfo,
  supplyPoolX,
  supplyPoolY,
  tickA,
  tickB,
  leverage,
  startL,
  maxL,
  maxLeverage,
  sanitizedXOnBlur,
  sanitizedYOnBlur,
}: UsePositionCreationValuesArgs) => {
  const positionCreationValues: PositionCreationValues | undefined = useMemo(() => {
    if (
      !pool ||
      !maxL ||
      !config ||
      tickA === undefined ||
      tickB === undefined ||
      !supplyPoolX ||
      !supplyPoolY ||
      !configInfo ||
      startL === undefined ||
      !maxLeverage
    ) {
      return undefined
    }

    // (maxL - startL) / (maxLeverage - 1) * (leverage - 1) + startL
    const liquidity = BigInt(
      new Decimal((maxL - startL).toString())
        .mul(new Decimal(leverage).sub(1))
        .div(new Decimal(maxLeverage).sub(1))
        .add(startL.toString())
        .toFixed(0, Decimal.ROUND_DOWN)
    )

    const toAmount = (value: Decimal, T: CoinInfo<PhantomTypeArgument>, roundUp = false) => {
      const round = roundUp ? Decimal.ROUND_UP : Decimal.ROUND_DOWN
      return Amount.fromInt(BigInt(value.toFixed(0, round)), T.decimals)
    }

    const UX = Amount.fromNum(sanitizedXOnBlur, config.info.X.decimals)
    const UY = Amount.fromNum(sanitizedYOnBlur, config.info.Y.decimals)

    const P0 = pool.currentPrice().numeric
    const pm = PositionDecimalMath.forNewPosition({
      tickA,
      tickB,
      P0,
      UX: new Decimal(UX.int.toString()),
      UY: new Decimal(UY.int.toString()),
      L: new Decimal(liquidity.toString()),
    })

    const marginLevel = pm.M(P0)

    const [pl_, ph_] = pm.plh(config.liqMargin)
    const pl = Price.fromNumeric(config.info.X, config.info.Y, pl_)
    const ph = Price.fromNumeric(config.info.X, config.info.Y, ph_)

    const DX = toAmount(pm.DX, config.info.X, true)
    const DY = toAmount(pm.DY, config.info.Y, true)

    const irx =
      Number(
        supplyPoolX.calcInterestRateAfterBorrowBps(configInfo.lendFacilCap, DX.int, Date.now()) ??
          supplyPoolX.getInterestModel(configInfo.lendFacilCap).valueAt(10000n).toString()
      ) / 10000
    const iry =
      Number(
        supplyPoolY.calcInterestRateAfterBorrowBps(configInfo.lendFacilCap, DY.int, Date.now()) ??
          supplyPoolY.getInterestModel(configInfo.lendFacilCap).valueAt(10000n)
      ) / 10000

    const effectiveInterestRate = calcEffectiveInterestRate({
      dx: DX,
      dy: DY,
      irx: new Decimal(irx),
      iry: new Decimal(iry),
      poolPrice: pool.currentPrice(),
    })

    const ret: PositionCreationValues = {
      liquidity,
      tickA,
      tickB,
      UX,
      UY,
      info: {
        pa: pool.tickToPrice(tickA),
        pb: pool.tickToPrice(tickB),
        X: toAmount(pm.X(P0), config.info.X),
        Y: toAmount(pm.Y(P0), config.info.Y),
        CX: toAmount(pm.CX, config.info.X),
        CY: toAmount(pm.CY, config.info.Y),
        DX,
        DY,
        marginLevel,
        leverage: pm.leverage(P0),
        pl,
        ph,
        interestRates: {
          x: new Decimal(irx),
          y: new Decimal(iry),
          effective: effectiveInterestRate,
        },
      },
    }
    return ret
  }, [
    pool,
    startL,
    maxL,
    config,
    tickA,
    tickB,
    leverage,
    maxLeverage,
    sanitizedXOnBlur,
    sanitizedYOnBlur,
    supplyPoolX,
    supplyPoolY,
    configInfo,
  ])

  const notEnoughSupply = useMemo(() => {
    if (!supplyPoolX || !supplyPoolY || !positionCreationValues || !configInfo) {
      return undefined
    }

    const insufficientSupplyTokens: string[] = []

    if (
      !configInfo.canBorrowX({
        borrowAmount: positionCreationValues.info.DX.int,
        supplyPoolX,
      })
    ) {
      insufficientSupplyTokens.push(configInfo.X.displaySymbol)
    }
    if (
      !configInfo.canBorrowY({
        borrowAmount: positionCreationValues.info.DY.int,
        supplyPoolY,
      })
    ) {
      insufficientSupplyTokens.push(configInfo.Y.displaySymbol)
    }

    if (insufficientSupplyTokens.length === 0) {
      return undefined
    }

    return `Not enough ${insufficientSupplyTokens.join(' and ')} supply in lending pool${insufficientSupplyTokens.length > 1 ? 's' : ''}`
  }, [supplyPoolX, supplyPoolY, positionCreationValues, configInfo])

  return {
    positionCreationValues,
    notEnoughSupply,
  }
}
